import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * Reads a `?<paramName>=<id>` query param and, after items have loaded,
 * scrolls the matching DOM element into view and adds a temporary ring
 * highlight that fades out. The element is found by `data-testid="${prefix}-${id}"`.
 *
 * Returns the highlighted id (string | null) so the page can also use it
 * for visual styling (e.g. a thicker border on the matching card).
 *
 * Usage:
 *   const highlightedId = useQueryParamHighlight('schedule', schedules, 'sched');
 */
export function useQueryParamHighlight(paramName, items, testIdPrefix) {
  const [searchParams] = useSearchParams();
  const targetId = searchParams.get(paramName);
  const [highlightedId, setHighlightedId] = useState(null);

  useEffect(() => {
    if (!targetId || !items || items.length === 0) return;
    // Wait one frame for the DOM to be present
    const t = setTimeout(() => {
      const el = document.querySelector(`[data-testid="${testIdPrefix}-${targetId}"]`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.classList.add('ring-2', 'ring-primary/60', 'ring-offset-2', 'ring-offset-background', 'transition-shadow');
        setHighlightedId(targetId);
        // Fade out the ring after ~3s
        setTimeout(() => {
          el.classList.remove('ring-2', 'ring-primary/60', 'ring-offset-2', 'ring-offset-background');
        }, 3200);
      }
    }, 200);
    return () => clearTimeout(t);
  }, [targetId, items, testIdPrefix]);

  return highlightedId;
}
